import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { LabelPrintService } from '../../../core/services/label-print.service';
import { PageTitleService } from '../../../core/services/page-title.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { BOOK_INVENTORY_TABLE_CONFIG } from '../table-configs';
import { DateFormatService } from '../../../core/services/date-format.service';

@Component({
  selector: 'app-book-inventory',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Module],
  templateUrl: './book-inventory.component.html',
  styleUrl: './book-inventory.component.scss',
})
export class BookInventoryComponent implements OnInit {
  records: any[] = [];
  isListLoading = false;

  // Author / category books panel (hyperlink drill-down)
  showAuthorPanel = false;
  authorPanelTitle = '';
  authorPanelBooks: any[] = [];
  isAuthorPanelLoading = false;
  authorPanelError = '';

  // Copies modal (within author panel)
  showCopiesModal = false;
  copiesModalBook: any = null;
  copiesModalLoading = false;
  copiesModalError = '';

  tableConfig: VT2_TABLE_CONFIG = {
    ...BOOK_INVENTORY_TABLE_CONFIG,
    maxActionButtons: 1,
    actionButtons: [
      { icon: '🖨', title: 'Print Label', cssClass: '', onClick: (row: any) => this.printLabel(row) },
    ],
    hyperlinkFields: {
      authorNames: {
        onClick: (value: any) => {
          const names: string[] = Array.isArray(value) ? value.filter(Boolean) : [String(value)];
          if (names.length) this.openAuthorPanel(names);
        },
      },
      categoryNames: {
        onClick: (value: any) => {
          const names: string[] = Array.isArray(value) ? value.filter(Boolean) : [String(value)];
          if (names.length) this.openCategoryPanel(names);
        },
      },
    },
  };

  constructor(
    private api: ApiService,
    private labelPrint: LabelPrintService,
    private pageTitle: PageTitleService,
    private router: Router,
    private dateFmt: DateFormatService,
  ) {}

  openCatalogue(): void {
    this.router.navigate(['/book-catalogue'], { state: { returnTo: '/book-inventory' } });
  }

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.pageTitle.set('Book Inventory', 'Physical copy status of all books');
    this.loadList();
  }

  loadList(): void {
    this.isListLoading = true;
    this.api.getAllBookInventory().subscribe({
      next: (data) => {
        this.records = (data || []).map(r => ({
          ...r,
          bookSrNo: Number(r.bookSrNo),
          statusDisplay: r.location || (r.status === 'C' ? 'At Centre' : r.status === 'M' ? 'With Member' : r.status),
        }));
        this.isListLoading = false;
      },
      error: () => {
        this.records = [];
        this.isListLoading = false;
      },
    });
  }

  printLabel(row: any): void {
    this._doPrint(row, [Number(row.bookSrNo)]);
  }

  private async _doPrint(book: any, srNos: number[]): Promise<void> {
    if (!srNos.length) return;
    const clientName = await this.labelPrint.resolveClientName();
    this.labelPrint.print(book?.bookName || '', srNos, clientName);
  }

  // ── Books Panel (author / category drill-down) ─────────────────────────────

  openAuthorPanel(names: string[]): void {
    this._openBooksPanel(`Books by ${names.join(', ')}`, names.map(n => this.api.locateBooksByAuthor(n)));
  }

  openCategoryPanel(names: string[]): void {
    this._openBooksPanel(`Books in ${names.join(', ')}`, names.map(n => this.api.getBooksByCategory(n)));
  }

  private _openBooksPanel(title: string, requests: any[]): void {
    this.authorPanelTitle = title;
    this.showAuthorPanel = true;
    this.authorPanelBooks = [];
    this.isAuthorPanelLoading = true;
    this.authorPanelError = '';
    this.showCopiesModal = false;

    forkJoin(requests).subscribe({
      next: (results) => {
        const seen = new Set<string>();
        const merged: any[] = [];
        (results as any[][]).flat().forEach((book: any) => {
          const key = book.bookId || book.bookName;
          if (!seen.has(key)) { seen.add(key); merged.push(book); }
        });
        this.authorPanelBooks = merged;
        this.isAuthorPanelLoading = false;
      },
      error: (err: any) => {
        this.authorPanelError = err?.message || 'Failed to load books.';
        this.isAuthorPanelLoading = false;
      },
    });
  }

  closeAuthorPanel(): void {
    this.showAuthorPanel = false;
    this.showCopiesModal = false;
  }

  openBookCopies(book: any): void {
    this.copiesModalError = '';
    this.showCopiesModal = true;

    if (Array.isArray(book.copies)) {
      this.copiesModalBook = book;
      this.copiesModalLoading = false;
      return;
    }

    this.copiesModalBook = { ...book, copies: null };
    this.copiesModalLoading = true;
    this.api.getBookCopies(book._id).subscribe({
      next: (copies) => { this.copiesModalBook.copies = copies || []; this.copiesModalLoading = false; },
      error: (err: any) => { this.copiesModalError = err?.message || 'Failed to load copies.'; this.copiesModalLoading = false; },
    });
  }

  closeCopiesModal(): void {
    this.showCopiesModal = false;
    this.copiesModalBook = null;
    this.copiesModalLoading = false;
    this.copiesModalError = '';
  }

  copyStatusLabel(status: string): string {
    if (status === 'C') return 'At Center';
    if (status === 'M') return 'Issued';
    if (status === 'S') return 'Scrapped';
    return status;
  }

  copyRowClass(status: string): string {
    if (status === 'C') return 'bm-hl-row-c';
    if (status === 'M') return 'bm-hl-row-m';
    if (status === 'S') return 'bm-hl-row-s';
    return '';
  }
}
